import { useQuery } from "@tanstack/react-query";
import { fetchApiGet } from "../utils";

// ── Query parameter types ──────────────────────────────────────────

export interface AgtechTicketsExpandedFilters {
  customer_ids?: string;
  scale_ticket_id?: string;
  scale_ticket_inbound_outbound?: string;
  ship_date?: string;
  plc_id?: string;
  commodity_id?: string;
  ship_from?: string;
  ship_to?: string;
  type?: "settlements" | "advances" | "all";
  page?: number;
  page_size?: number;
}

// ── Response types ─────────────────────────────────────────────────

export interface AgtechTicketsExpandedPagination {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export type AgtechTicketsContractsInfo = AgtechTicketsContractInfo[];

export interface AgtechTicketsContractInfo {
  entity_type: string;
  contract_nbr: number;
  contract_id: number;
  pricing_type_code: number;
  contract_date: number;
  settlement_nbr: number;
  pricing_order: number;
  balance_forward: string;
  applied: string;
  gross_dollars: string;
  discounts: string;
  charges: string;
  deferred_payment: string;
  price: string;

  pricing?: { quantity: string; bot_uom: string; market_month: string } | null;
  pricingRelease?: { quantity: string; } | null;
  release?: { quantity: string; } | null;
}

export interface AgtechTicketsExpandedResponse {
  tickets: {
    data: ExpandedTransformedTicket[];
    pagination: AgtechTicketsExpandedPagination;
  };
  contractsInfo: AgtechTicketsContractsInfo;
  addDedsInfo: AddDedSummary[];
}

// ── Add/Ded enrichment types ──────────────────────────────────────

export interface CommodityDeductionAdditionLookup {
  entity_id: string;
  commodity_id: string;
  deduction_addition_code: string;
  da_table_nbr: number;
  description: string | null;
  rate: string | null;
  rate_mode: string | null;
  rate_type_code: string | null;
  weight_basis: string | null;
  apply_on_inbound_ticket_flag: string | null;
  apply_on_outbound_ticket_flag: string | null;
  row_data: unknown;
}

export interface AddDedSplitCustomerRef {
  customer_id: string;
  long_name: string;
}

export interface PaymentMethodLookup {
  code: string;
  description: string;
}

export interface AddDedLocationRef {
  long_name: string;
}

export interface AddDedSplit {
  sort_order: number;
  payee_id: string | null;
  vendor_customer_id: string | null;
  settlement_amount: string | null;
  less_other_amount: string | null;
  split_percent: string | null;
  payment_method: string | null;
  payment_amount: string | null;
  payment_amount_base_currency: string | null;
  remit_or_bill_to_id: string | null;
  payment_split_id: string | null;
  PayeeCustomer: AddDedSplitCustomerRef | null;
  VendorCustomer: AddDedSplitCustomerRef | null;
  RemitOrBillToCustomer: AddDedSplitCustomerRef | null;
  PaymentSplitCustomer: AddDedSplitCustomerRef | null;
  PaymentMethod: PaymentMethodLookup | null;
}

export interface AddDedRow {
  deduction_addition_code: string;
  plc_id: string | null;
  amount: string | null;
  description: string | null;
  charge_group: string | null;
  commodityRates: CommodityDeductionAdditionLookup[];
  Location: AddDedLocationRef | null;
}

export interface AddDedSummary {
  entity_type: "settlement" | "advance";
  entity_id_nbr: number;
  rows: AddDedRow[];
  splits: AddDedSplit[];
  total_amount: string;
}

export interface ExpandedTransformedTicket {
  scale_ticket_id: string;
  entries: ExpandedTransformedEntry[];
}

export interface ExpandedTransformedEntry {
  base_uom: string;
  dest_uom: string;
  commodity_conversion_factor: string | null,
  quantity_entry_nbr: number;
  scale_ticket_id: string | null;
  scale_ticket_gross_weight: string | null;
  scale_ticket_tare_weight: string | null;
  scale_quantity_lb: string | null;
  vendor_id: string;
  customer_id: string | null;
  Customer: { long_name: string } | null;
  vehicle_id: string | null;
  plc_id: string;
  Location: { long_name: string } | null;
  commodity_id: string;
  commodity_display_name: string | null;
  reference_code: string | null;
  ship_date: string | null;
  add_date: string | null;
  scale_uom_code: string | null;
  report_uom_code: string | null;
  train_barge: string | null;
  ship_from_id: string | null;
  ship_to_id: string | null;
  ShipFrom: { long_name: string } | null;
  ShipTo: { long_name: string } | null;
  grades: ExpandedChargeGradeDetail[];
  splits: TransformedSplit[];
  settlements: ExpandedTransformedSettlement[];
  advances: ExpandedTransformedAdvance[];
}

export interface TransformedSplit {
  ticket_related_id: string | null;
  split_percentage: string | null;
  scale_quantity_lb: string | null;
  TicketRelation?: { long_name: string } | null;
  applied_quantity_lb: string | null;
  shrink_quantity_lb: string | null;
  applied_quantity: string | null;
  shrink_quantity: string | null;
}

export interface ContractInfo {
  contract_id: number;
  vendor_customer_id: string;
  quantity: string | null;
  contract_type: string;
  pricing_type_code: string;
  contract_date: Date | null;
}

export interface ExpandedTransformedSettlement {
  settlement_nbr: number;
  serial_nbr: number | null;
  uom_code: string | null;
  contract_nbr: number;
  contract_id?: string | null;
  vehicle_disposition_nbr: number;
  pricing_order: number;
  vp_quantity: string | null;
  vp_applied_quantity: string | null;
  deleted_flag: string | null;
  ship_from_id: string | null;
  ship_to_id: string | null;
  ShipFrom: { long_name: string } | null;
  ShipTo: { long_name: string } | null;
  grades: ExpandedGrade[];
  contract?: ContractInfo | null;
  price: string | null;
  net_price: string | null;
  discounts_price: string | null;
  cash_charges: string | null;
  charges_details: string | null;
  cash_applied: string | null;
  cash_settlement_amount: string | null;
}

export interface ExpandedTransformedAdvance {
  advance_nbr: number;
  serial_nbr: number | null;
  uom_code: string | null;
  contract_nbr: number;
  contract_id?: string | null;
  vehicle_disposition_nbr: number;
  pricing_order: number;
  vp_quantity: string | null;
  vp_applied_quantity: string | null;
  deleted_flag: string | null;
  ship_from_id: string | null;
  ship_to_id: string | null;
  ShipFrom: { long_name: string } | null;
  ShipTo: { long_name: string } | null;
  grades: ExpandedGrade[];
  contract?: ContractInfo | null;
  price: string | null;
  net_price: string | null;
  discounts_price: string | null;
  cash_charges: string | null;
  charges_details: string | null;
  cash_applied: string | null;
  cash_advance_amount: string | null;
}

export interface ExpandedGrade {
  charge_grade_code: string;
  charge_grade_value: string | null;
}

export interface ExpandedChargeGradeDetail {
  charge_grade_code: string;
  charge_grade_value_o: string | null;
  charge_grade_value_d: string | null;
}

// ── Query param builder ────────────────────────────────────────────

function buildQueryParams(filters: AgtechTicketsExpandedFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.customer_ids) params.append("customer_ids", filters.customer_ids);
  if (filters.scale_ticket_id) params.append("scale_ticket_id", filters.scale_ticket_id);
  if (filters.scale_ticket_inbound_outbound) params.append("scale_ticket_inbound_outbound", filters.scale_ticket_inbound_outbound);
  if (filters.ship_date) params.append("ship_date", filters.ship_date);
  if (filters.plc_id) params.append("plc_id", filters.plc_id);
  if (filters.commodity_id) params.append("commodity_id", filters.commodity_id);
  if (filters.ship_from) params.append("ship_from", filters.ship_from);
  if (filters.ship_to) params.append("ship_to", filters.ship_to);
  if (filters.type) params.append("type", filters.type);
  if (filters.page !== undefined) params.append("page", filters.page.toString());
  if (filters.page_size !== undefined) params.append("page_size", filters.page_size.toString());

  return params;
}

// ── React Query hook ───────────────────────────────────────────────

export function useListAgtechTicketsExpanded(filters: AgtechTicketsExpandedFilters) {
  const queryParams = buildQueryParams(filters);

  return useQuery({
    queryKey: ["agtech", "tickets", "expanded", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechTicketsExpandedResponse>("agtech/tickets/expanded", queryParams),
    enabled: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: 1,
  });
}

// ── By-contract query ──────────────────────────────────────────────

export interface AgtechTicketsByContractFilters {
  contract_nbrs: number[];
  page?: number;
  page_size?: number;
}

function buildByContractQueryParams(filters: AgtechTicketsByContractFilters): URLSearchParams {
  const params = new URLSearchParams();

  params.append("contract_nbrs", filters.contract_nbrs.join(","));
  if (filters.page !== undefined) params.append("page", filters.page.toString());
  if (filters.page_size !== undefined) params.append("page_size", filters.page_size.toString());

  return params;
}

export function useListAgtechTicketsByContract(filters: AgtechTicketsByContractFilters) {
  const queryParams = buildByContractQueryParams(filters);

  return useQuery({
    queryKey: ["agtech", "tickets", "by-contract", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechTicketsExpandedResponse>("agtech/tickets/by-contract", queryParams),
    enabled: filters.contract_nbrs.length > 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    retry: 1,
  });
}

// ── By-settlement-or-advance query ─────────────────────────────────

export interface AgtechTicketsBySettlementOrAdvanceFilters {
  settlement_nbrs?: number[];
  advance_nbrs?: number[];
  page?: number;
  page_size?: number;
}

function buildBySettlementOrAdvanceQueryParams(filters: AgtechTicketsBySettlementOrAdvanceFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.settlement_nbrs && filters.settlement_nbrs.length > 0) {
    params.append("settlement_nbrs", filters.settlement_nbrs.join(","));
  }
  if (filters.advance_nbrs && filters.advance_nbrs.length > 0) {
    params.append("advance_nbrs", filters.advance_nbrs.join(","));
  }
  if (filters.page !== undefined) params.append("page", filters.page.toString());
  if (filters.page_size !== undefined) params.append("page_size", filters.page_size.toString());

  return params;
}

export function useListAgtechTicketsBySettlementOrAdvance(filters: AgtechTicketsBySettlementOrAdvanceFilters) {
  const queryParams = buildBySettlementOrAdvanceQueryParams(filters);

  const hasSettlements = filters.settlement_nbrs && filters.settlement_nbrs.length > 0;
  const hasAdvances = filters.advance_nbrs && filters.advance_nbrs.length > 0;

  return useQuery({
    queryKey: ["agtech", "tickets", "by-settlement-or-advance", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechTicketsExpandedResponse>("agtech/tickets/by-settlement", queryParams),
    enabled: hasSettlements || hasAdvances,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    retry: 1,
  });
}

// ── Settlements list query ─────────────────────────────────────────

export interface AgtechSettlementsFilters {
  commodity_id?: string;
  customer_ids?: string;
  serial_settlement_nbrs?: string;
  serial_advance_nbrs?: string;
  type?: "settlements" | "advances" | "all";
  page?: number;
  page_size?: number;
}

export interface SettlementRow {
  id: string;
  entity_type: string;
  entity_id: string;
  settlement_nbr: number;
  serial_nbr: number | null;
  void_nbr: number | null;
  uom_code: string | null;
  currency_code: string | null;
  average_flag: string | null;
  check_flag: string | null;
  settlement_type: string | null;
  due_date: string | null;
  settlement_date: string | null;
  void_date: string | null;
  settlement_amount: string | null;
  advance_settlement_nbr: number | null;
  advance_amount: string | null;
  vendor_id: string | null;
  commodity_id: string | null;
  created_at: string;
  updated_at: string;
  Customer: { id: string; long_name: string } | null;
}

export interface AdvanceRow {
  id: string;
  entity_type: string;
  entity_id: string;
  advance_nbr: number;
  serial_nbr: number | null;
  void_nbr: number | null;
  uom_code: string | null;
  currency_code: string | null;
  average_flag: string | null;
  check_flag: string | null;
  advance_type: string | null;
  due_date: string | null;
  advance_date: string | null;
  void_date: string | null;
  advance_amount: string | null;
  vendor_id: string | null;
  commodity_id: string | null;
  created_at: string;
  updated_at: string;
  Customer: { id: string; long_name: string } | null;
}

export interface SettlementContractInfo {
  entity_type: string;
  contract_nbr: number;
  settlement_nbr: number;
  pricing_order: number;
  balance_forward: string | null;
  applied: string | null;
  gross_dollars: string | null;
  discounts: string | null;
  charges: string | null;
  deferred_payment: string | null;
  price: string | null;
  contract_id: number | null;
  contract_date: string | null;
  quantity: string | null;
  pricing_type_code: string | null;
  settlement_amount: string;
  pricing: {
    quantity: string | null;
    bot_uom: string | null;
    market_month: string | null;
  } | null;
  pricingRelease: {
    quantity: string | null;
  } | null;
  release: {
    release_quantity: string | null;
    market_zone: string | null;
    sched_ship_date: string | null;
    sched_ship_end_date: string | null;
  } | null;
}

export type SettlementOrAdvanceItem =
  | {
      type: "settlement";
      settlement: SettlementRow;
      contractSummaryInfo: SettlementContractInfo[];
    }
  | {
      type: "advance";
      advance: AdvanceRow;
      contractSummaryInfo: AdvanceContractInfo[];
    };

export interface AgtechSettlementsResponse {
  data: SettlementOrAdvanceItem[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
}

function buildSettlementsQueryParams(filters: AgtechSettlementsFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.commodity_id) params.append("commodity_id", filters.commodity_id);
  if (filters.customer_ids) params.append("customer_ids", filters.customer_ids);
  if (filters.serial_settlement_nbrs) params.append("serial_settlement_nbrs", filters.serial_settlement_nbrs);
  if (filters.serial_advance_nbrs) params.append("serial_advance_nbrs", filters.serial_advance_nbrs);
  if (filters.type) params.append("type", filters.type);
  if (filters.page !== undefined) params.append("page", filters.page.toString());
  if (filters.page_size !== undefined) params.append("page_size", filters.page_size.toString());

  return params;
}

export function useListAgtechSettlements(filters: AgtechSettlementsFilters) {
  const queryParams = buildSettlementsQueryParams({ ...filters, type: "settlements" });

  return useQuery({
    queryKey: ["agtech", "tickets", "settlements", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechSettlementsResponse>("agtech/tickets/settlements", queryParams),
    enabled: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: 1,
  });
}

export function useListAgtechAdvances(filters: AgtechSettlementsFilters) {
  const queryParams = buildSettlementsQueryParams({ ...filters, type: "advances" });

  return useQuery({
    queryKey: ["agtech", "tickets", "advances", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechSettlementsResponse>("agtech/tickets/settlements", queryParams),
    enabled: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: 1,
  });
}

// ── By-advance query (uses new /agtech/tickets/by-advance route) ──

export interface AdvanceContractInfo {
  entity_type: string;
  contract_nbr: number;
  advance_nbr: number;
  pricing_order: number;
  balance_forward: string | null;
  applied: string | null;
  gross_dollars: string | null;
  discounts: string | null;
  charges: string | null;
  deferred_payment: string | null;
  price: string | null;
  contract_id: number | null;
  contract_date: string | null;
  quantity: string | null;
  pricing_type_code: string | null;
  advance_amount: string;
  pricing: {
    quantity: string | null;
    bot_uom: string | null;
    market_month: string | null;
  } | null;
  pricingRelease: {
    quantity: string | null;
  } | null;
  release: {
    release_quantity: string | null;
    market_zone: string | null;
    sched_ship_date: string | null;
    sched_ship_end_date: string | null;
  } | null;
}

export interface AgtechTicketsByAdvanceResponse {
  tickets: {
    data: ExpandedTransformedTicket[];
    pagination: AgtechTicketsExpandedPagination;
  };
  contractsInfo: AdvanceContractInfo[];
  addDedsInfo: AddDedSummary[];
}

export interface AgtechTicketsByAdvanceFilters {
  advance_nbrs: number[];
  page?: number;
  page_size?: number;
}

function buildByAdvanceQueryParams(filters: AgtechTicketsByAdvanceFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.advance_nbrs.length > 0) {
    params.append("advance_nbrs", filters.advance_nbrs.join(","));
  }
  if (filters.page !== undefined) params.append("page", filters.page.toString());
  if (filters.page_size !== undefined) params.append("page_size", filters.page_size.toString());

  return params;
}

export function useListAgtechTicketsByAdvance(filters: AgtechTicketsByAdvanceFilters) {
  const queryParams = buildByAdvanceQueryParams(filters);

  return useQuery({
    queryKey: ["agtech", "tickets", "by-advance", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechTicketsByAdvanceResponse>("agtech/tickets/by-advance", queryParams),
    enabled: filters.advance_nbrs.length > 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    retry: 1,
  });
}
